// @file Network requests related to attachment captions
import { poll } from '@@/bits/polling'
import FetchableObject from '@@/surface/api/fetchable_object'
import { getApiFetchCommonOptions } from '@@/surface/api_fetch'
import { HTTPMethod } from '@padlet/fetch'

const API_VERSION = 1

class VideoSubtitlesApi extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/wishes/\${ wishHashid }/subtitles`
  }

  public static async download(wishHashid: string): Promise<{ blob: Blob; filename: string }> {
    const response = await fetch(this.buildUrl(this.url, { wishHashid }), {
      method: HTTPMethod.get,
    })

    if (response.status !== 200) {
      throw new Error('Subtitles failed to download')
    }

    const blob = await response.blob()
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = 'download' // default filename

    if (contentDisposition != null) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch?.[1] != null) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    return { blob, filename }
  }

  public static async update(
    wishHashid: string,
    subtitleFile: File,
  ): Promise<{ subtitleId?: string; waitForModerationResult?: boolean; status: number }> {
    const formData = new FormData()
    formData.append('wish_hashid', wishHashid)
    formData.append('subtitle_file', subtitleFile)

    const response = await fetch(this.buildUrl(this.url, { wishHashid }), {
      method: HTTPMethod.post,
      body: formData,
      ...getApiFetchCommonOptions(),
    })

    const responseJson = await response.json()
    return {
      subtitleId: responseJson.subtitle_id,
      waitForModerationResult: responseJson.wait_for_moderation_result,
      status: response.status,
    }
  }

  public static async pollModerationStatus(
    subtitleId: string,
    maxAttempts = 10, // Arbitrary limit to prevent infinite polling
    intervalSecs = 1,
  ): Promise<{
    state: string
    reason: string
  }> {
    try {
      const response = await poll({
        pollingUrl: `/api/${API_VERSION}/subtitles/${subtitleId}/moderation-status`,
        validationCallback: (response) => {
          const moderationState = response.moderation_state
          // We always expect moderation state from response
          if (moderationState == null) {
            throw new Error(`Unable to get subtitle moderation state`)
          }
          return moderationState.state !== 'pending'
        },
        options: {
          intervalSecs,
          maxAttempts,
          method: HTTPMethod.get,
        },
      })

      return response?.moderation_state
    } catch (e) {
      if (e == null) {
        return { state: 'rejected', reason: 'Unknown error' }
      }
      // Rethrow server errors and other exceptions
      throw e
    }
  }
}

export { VideoSubtitlesApi }
